import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Message } from "./ui/Message";
import { getLogoSrc } from "@/utils/helpers";
import { MessageSquare } from "lucide-react";

export const ChatMessages = ({
  messages,
  isLoading,
  showMetadata = false,
  widgetConfig = null,
  hasMoreMessages = false,
  isLoadingMoreMessages = false,
  onLoadMoreMessages = null,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const lastMessageRef = useRef(null);
  const previousMessagesLength = useRef(0);
  const hasScrolledOnMount = useRef(false);
  const scrollContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const previousScrollHeight = useRef(0);

  useLayoutEffect(() => {
    if (messages.length > 0 && !hasScrolledOnMount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      hasScrolledOnMount.current = true;
      previousMessagesLength.current = messages.length;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!onLoadMoreMessages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreMessages &&
          !isLoadingMoreMessages
        ) {
          if (scrollContainerRef.current) {
            previousScrollHeight.current =
              scrollContainerRef.current.scrollHeight;
          }
          onLoadMoreMessages();
        }
      },
      { threshold: 1.0 },
    );

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (loadMoreTriggerRef.current) {
        observer.unobserve(loadMoreTriggerRef.current);
      }
    };
  }, [hasMoreMessages, isLoadingMoreMessages, onLoadMoreMessages]);

  useEffect(() => {
    if (
      isLoadingMoreMessages === false &&
      previousScrollHeight.current > 0 &&
      scrollContainerRef.current
    ) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeight.current;
      scrollContainerRef.current.scrollTop = scrollDiff;
      previousScrollHeight.current = 0;
    }
  }, [isLoadingMoreMessages]);

  useEffect(() => {
    const currentLength = messages.length;
    const previousLength = previousMessagesLength.current;
    const lastMessage = messages[currentLength - 1];

    const isConversationLoad = currentLength > previousLength + 1;

    if (currentLength > 0 && hasScrolledOnMount.current) {
      if (isConversationLoad) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
        }, 150);
      } else if (currentLength > previousLength && lastMessage) {
        if (lastMessage.role === "user") {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        } else if (
          lastMessage.role === "assistant" &&
          lastMessage.isStreaming
        ) {
          setTimeout(() => {
            lastMessageRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 100);
        }
      }
    }

    previousMessagesLength.current = currentLength;
  }, [messages]);

  const companyName =
    widgetConfig?.widget_customization?.branding?.company_name || "TIA GPC";
  const companyLogo =
    widgetConfig?.widget_customization?.branding?.company_logo_base64;
  const customWelcomeMessage =
    widgetConfig?.widget_customization?.welcome_message;

  const logoSrc = getLogoSrc(companyLogo);

  const welcomeMessage =
    customWelcomeMessage || t("chat.welcome", { companyName });

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="gpc-messages-container">
        <div className="gpc-empty-state">
          {logoSrc ? (
            <div className="gpc-welcome-logo">
              <img
                src={logoSrc}
                alt={companyName}
                className="gpc-welcome-company-logo"
              />
            </div>
          ) : (
            <MessageSquare className="gpc-empty-icon" />
          )}
          <h3 className="gpc-empty-title">{welcomeMessage}</h3>
          <p className="gpc-empty-text">{t("chat.emptyState")}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="gpc-messages-container gpc-scrollbar gpc-smooth-scroll"
    >
      {/* Loading indicator y trigger para scroll infinito (arriba) */}
      {hasMoreMessages && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          {isLoadingMoreMessages ? (
            <div className="gpc-loading-more-messages">
              <div className="gpc-loading-spinner"></div>
              <span>{t("chat.loadingMore", "Loading older messages...")}</span>
            </div>
          ) : (
            <div ref={loadMoreTriggerRef} style={{ height: "1px" }} />
          )}
        </div>
      )}

      {messages.map((message, index) => (
        <Message
          key={message.id}
          message={message}
          showMetadata={showMetadata}
          messageRef={index === messages.length - 1 ? lastMessageRef : null}
        />
      ))}

      <div ref={messagesEndRef} />
    </div>
  );
};
